import { httpClient, ApiError } from '../../utils/http'
import { BATCH_SIZE, CONCURRENT_REQUESTS, ProgressCallback } from './types'
import { processBatchesConcurrent } from './batch-processing'
import { handleErrorWithNotification } from '../../utils/error-notification'

// Re-export concurrent request count for use in other modules
export { CONCURRENT_REQUESTS }

// Type definitions for API responses
export interface WiseApiResponse {
    data: (string | number | null)[][]
}

export interface WisePriceApiResponse {
    data: (string | number | null)[][]
}

// Request deduplication cache
interface PendingRequest<T> {
    promise: Promise<T>
    timestamp: number
}

const pendingRequests = new Map<string, PendingRequest<unknown>>()
const REQUEST_CACHE_TTL = 1000 // 1 second TTL for deduplication

/**
 * Generate a cache key for request deduplication
 */
function generateCacheKey(
    endpoint: string,
    payload: Record<string, unknown>
): string {
    return `${endpoint}:${JSON.stringify(payload)}`
}

/**
 * Clean up expired cache entries
 */
function cleanupExpiredRequests(): void {
    const now = Date.now()
    const keysToDelete: string[] = []
    pendingRequests.forEach((request, key) => {
        if (now - request.timestamp > REQUEST_CACHE_TTL) {
            keysToDelete.push(key)
        }
    })
    keysToDelete.forEach((key) => pendingRequests.delete(key))
}

/**
 * Execute a request with deduplication
 * If an identical request is already in flight, return the same promise
 */
async function deduplicatedRequest<T>(
    cacheKey: string,
    requestFn: () => Promise<T>
): Promise<T> {
    // Cleanup old entries periodically
    cleanupExpiredRequests()

    // Check if we have a pending request for this key
    const pending = pendingRequests.get(cacheKey) as
        | PendingRequest<T>
        | undefined
    if (pending) {
        return pending.promise
    }

    // Create new request
    const promise = requestFn().finally(() => {
        // Remove from cache after completion
        pendingRequests.delete(cacheKey)
    })

    pendingRequests.set(cacheKey, {
        promise,
        timestamp: Date.now(),
    })

    return promise
}

/**
 * Single batch API call for WISE
 */
export const getWiseApiDataBatch = async (
    symbols: string[][],
    parameters: string[],
    period: string,
    quarter: string | null,
    divisor: string | null,
    signal?: AbortSignal
): Promise<(string | number | null)[][]> => {
    const payload = {
        symbols,
        parameters,
        periods: [period],
        quarter: quarter ? [quarter] : null,
        divisor: divisor ? [divisor] : null,
        platform: 'EXCEL',
    }

    try {
        const response = await httpClient.post<(string | number | null)[][]>(
            '/api/private/functions/wise',
            payload,
            { signal }
        )
        return response.data
    } catch (error) {
        if (error instanceof ApiError && error.code === 'ABORTED') {
            throw new Error('Operation cancelled')
        }
        if (
            error instanceof Error &&
            (error.name === 'CanceledError' || signal?.aborted)
        ) {
            throw new Error('Operation cancelled')
        }

        handleErrorWithNotification(
            error,
            {
                operation: 'wise-api-batch',
                symbolsCount: symbols.length,
                parametersCount: parameters.length,
            },
            {
                showNotification: true,
            }
        )

        return symbols.map(() => parameters.map(() => 'Error fetching data'))
    }
}

/**
 * Batched WISE API call with progress reporting
 */
export const getWiseApiData = async (
    symbols: string[][],
    parameters: string[],
    period: string,
    quarter: string | null,
    divisor: string | null,
    onProgress?: ProgressCallback,
    signal?: AbortSignal
): Promise<(string | number | null)[][]> => {
    // For small requests, use direct call with deduplication
    if (symbols.length <= BATCH_SIZE) {
        const cacheKey = generateCacheKey('/api/private/functions/wise', {
            symbols,
            parameters,
            period,
            quarter,
            divisor,
        })

        return deduplicatedRequest(cacheKey, () =>
            getWiseApiDataBatch(
                symbols,
                parameters,
                period,
                quarter,
                divisor,
                signal
            )
        )
    }

    // For large requests, batch process
    const batchResults = await processBatchesConcurrent(
        symbols,
        BATCH_SIZE,
        async (batch) =>
            getWiseApiDataBatch(
                batch,
                parameters,
                period,
                quarter,
                divisor,
                signal
            ),
        CONCURRENT_REQUESTS,
        onProgress,
        undefined, // onBatchStart - not used here
        undefined, // onBatchComplete - not used here, handled at higher level
        signal
    )

    // Flatten batch results into single array
    return batchResults.flat()
}

/**
 * Single batch API call for WISEPRICE
 */
export const getWisePriceApiDataBatch = async (
    symbols: string[][],
    parameters: string[],
    numDaysToEnd?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    signal?: AbortSignal
): Promise<(string | number | null)[][]> => {
    const payload = {
        symbols,
        parameters,
        numDaysToEnd: numDaysToEnd || null,
        startDate: startDate || null,
        endDate: endDate || null,
        platform: 'EXCEL',
    }

    try {
        const response = await httpClient.post<(string | number | null)[][]>(
            '/api/private/functions/wise-price',
            payload,
            { signal }
        )
        return response.data
    } catch (error) {
        if (error instanceof ApiError && error.code === 'ABORTED') {
            throw new Error('Operation cancelled')
        }
        if (
            error instanceof Error &&
            (error.name === 'CanceledError' || signal?.aborted)
        ) {
            throw new Error('Operation cancelled')
        }

        handleErrorWithNotification(
            error,
            {
                operation: 'wiseprice-api-batch',
                symbolsCount: symbols.length,
                parametersCount: parameters.length,
            },
            {
                showNotification: true,
            }
        )

        return symbols.map(() => parameters.map(() => 'Error fetching data'))
    }
}

/**
 * Batched WISEPRICE API call with progress reporting
 */
export const getWisePriceApiData = async (
    symbols: string[][],
    parameters: string[],
    numDaysToEnd?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    onProgress?: ProgressCallback,
    signal?: AbortSignal
): Promise<(string | number | null)[][]> => {
    // For small requests, use direct call with deduplication
    if (symbols.length <= BATCH_SIZE) {
        const cacheKey = generateCacheKey('/api/private/functions/wise-price', {
            symbols,
            parameters,
            numDaysToEnd,
            startDate,
            endDate,
        })

        return deduplicatedRequest(cacheKey, () =>
            getWisePriceApiDataBatch(
                symbols,
                parameters,
                numDaysToEnd,
                startDate,
                endDate,
                signal
            )
        )
    }

    // For large requests, batch process
    const batchResults = await processBatchesConcurrent(
        symbols,
        BATCH_SIZE,
        async (batch) =>
            getWisePriceApiDataBatch(
                batch,
                parameters,
                numDaysToEnd,
                startDate,
                endDate,
                signal
            ),
        CONCURRENT_REQUESTS,
        onProgress,
        undefined, // onBatchStart - not used here
        undefined, // onBatchComplete - not used here, handled at higher level
        signal
    )

    // Flatten batch results into single array
    return batchResults.flat()
}

/**
 * Clear all pending requests (useful during cleanup or cancellation)
 */
export function clearPendingApiRequests(): void {
    pendingRequests.clear()
}
